import MechanismIcon from '@mui/icons-material/PrecisionManufacturing';
import { useTranslation } from 'react-i18next';
import CodeContent, { CodeSnippet } from '../Shared/CodeContent';
import { DriveBaseMechanismIcon, LinearMechanismIcon, RotationalMechanismIcon } from '../Devices/Mechanisms/mechanismIcons';

const MECHANISM_SNIPPETS = [
  {
    key: 'drive_base',
    titleFallback: 'Drive Base',
    codeFallback: 'drive_base = DriveBase()',
    descriptionFallback: 'Controls how the robot drives and orients on the field.',
    moreInfoFallback:
      'The Drive Base represents the robot drivetrain. Use it to choose the drive style, reference frame, maximum speeds, and controller behavior.\\n\\nCommon settings:\\n\\n• Drive type\\n\\n• Reference frame\\n\\n• Maximum drive speed\\n\\n• Maximum rotation speed\\n\\nTypical setup:\\n```python\\ndrive_base = DriveBase()\\n```',
    iconComponent: DriveBaseMechanismIcon,
    frameworkClassName: 'DriveBase',
  },
  {
    key: 'rotational_mechanism',
    titleFallback: 'Rotational Mechanism',
    codeFallback: 'rotational_mechanism = RotationalMechanism(motor_port=1)',
    descriptionFallback: 'Rotates an output shaft to a target position or speed.',
    moreInfoFallback:
      'A Rotational Mechanism is used for arms, intakes, pivots, and other mechanisms that turn around an axis. Configure limits when the mechanism needs to avoid moving too far.\\n\\nCommon settings:\\n\\n• Motor port\\n\\n• Motor type and direction\\n\\n• Gear ratio\\n\\n• Position limits\\n\\n• Velocity limits',
    iconComponent: RotationalMechanismIcon,
    frameworkClassName: 'RotationalMechanism',
  },
  {
    key: 'linear_mechanism',
    titleFallback: 'Linear Mechanism',
    codeFallback: 'linear_mechanism = LinearMechanism(motor_port=1)',
    descriptionFallback: 'Moves a mechanism linearly using a motor-driven output.',
    moreInfoFallback:
      'A Linear Mechanism converts motor rotation into straight-line travel. Use it for slides, lifts, elevators, or other mechanisms that move along a path. Configure limits when the mechanism needs to avoid moving too far.\\n\\nCommon settings:\\n\\n• Motor port\\n\\n• Motor type and direction\\n\\n• Gear ratio\\n\\n• Distance per output rotation\\n\\n• Position limits',
    iconComponent: LinearMechanismIcon,
    frameworkClassName: 'LinearMechanism',
  },
] as const;

const MechanismsContent = () => {
  const { t } = useTranslation();

  const snippet: CodeSnippet = {
    key: 'mechanisms',
    title: 'Mechanisms',
    icon: <MechanismIcon />,
    type: 'mechanism',
    children: MECHANISM_SNIPPETS.map((item) => {
      const base = `snippets.mechanisms.children.${item.key}`;

      return {
        key: item.key,
        title: t(`${base}.title`, item.titleFallback),
        code: t(`${base}.code`, item.codeFallback),
        description: t(`${base}.description`, item.descriptionFallback),
        more_info: t(`${base}.more_info`, item.moreInfoFallback),
        iconComponent: item.iconComponent,
        frameworkClassName: item.frameworkClassName,
      };
    }),
  };

  return <CodeContent snippet={snippet} commandManagementEnabled />;
};

export default MechanismsContent;
